import { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, BarChart3, Code, Save, Maximize2, Minimize2 } from 'lucide-react';
import DataViewChat from '../components/data-views/DataViewChat';
import ReconciliationPanel from '../components/data-views/ReconciliationPanel';
import KpiPanel from '../components/data-views/KpiPanel';
import PnlChartView from '../components/data-views/PnlChartView';
import AgentPageLayout from '../components/shared/AgentPageLayout';
import ItemSidebar from '../components/shared/ItemSidebar';
import Workspace from '../components/shared/Workspace';
import DataTable from '../components/shared/DataTable';
import QueryViewer from '../components/shared/QueryViewer';
import DrillDownPanel from '../components/shared/DrillDownPanel';
import { chatTheme as th } from '../theme/chat';
import { dataViewApi } from '../services/dataViewApi';
import { useCompany } from '../contexts/CompanyContext';

export default function DataViewsPage() {
  // View list
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);

  // Active view state
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');
  const [viewReportType, setViewReportType] = useState('custom');
  const [draftSteps, setDraftSteps] = useState([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [stepResults, setStepResults] = useState({});

  // Parameters (runtime values for parameterized steps)
  const [viewParameters, setViewParameters] = useState({});

  // Companies (from shared context)
  const { selectedCompanies, addCompany, removeCompany, setCompanies } = useCompany();

  // Realm cache for resolving rf_id -> company_name
  const realmsRef = useRef([]);
  useEffect(() => {
    const DATA_ENGINE = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
    fetch(`${DATA_ENGINE}/api/query/realms`).then(r => r.json())
      .then(data => { realmsRef.current = Array.isArray(data.data) ? data.data : (data.realms || []); })
      .catch(() => {});
  }, []);

  const resolveCompanies = useCallback((rfIds) => {
    return (rfIds || []).map(id => {
      const realm = realmsRef.current.find(r => r.rf_id === id);
      return { rf_id: id, company_name: realm?.company_name || id, source_type: realm?.source_type || 'QUICKBOOKS' };
    });
  }, []);

  // Thread
  const [activeThreadId, setActiveThreadId] = useState(null);

  // Chat item name for sidebar header
  const [chatItemName, setChatItemName] = useState('');

  // Loading / flags
  const [loading, setLoading] = useState(false);

  // Workspace UI state
  const [editorOpen, setEditorOpen] = useState(true);
  const [drillCell, setDrillCell] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  // KPI panel
  const [kpiOpen, setKpiOpen] = useState(false);

  // ── Load view list on mount ──────────────────────────────────────────────
  const loadViews = useCallback(async () => {
    try {
      const data = await dataViewApi.getViews('default', 'admin');
      const list = Array.isArray(data) ? data : (data.items || []);
      setViews(list);
    } catch (err) {
      console.error('Failed to load views:', err);
    }
  }, []);

  useEffect(() => { loadViews(); }, [loadViews]);

  // ── Select a view ────────────────────────────────────────────────────────
  const handleSelectView = useCallback(async (id) => {
    setActiveViewId(id);
    setStepResults({});
    setViewParameters({});
    setActiveStepIndex(0);
    try {
      const view = await dataViewApi.getView(id);
      setViewName(view.name || '');
      setViewDescription(view.description || '');
      setViewReportType(view.report_type || 'custom');
      setDraftSteps(view.steps || []);
      if (view.rf_ids?.length) {
        setCompanies(resolveCompanies(view.rf_ids));
      }
      setActiveThreadId(view.thread_id || null);
    } catch (err) {
      console.error('Failed to load view:', err);
    }
  }, [resolveCompanies, setCompanies]);

  // ── New view -> reset state ──────────────────────────────────────────────
  const handleNewView = useCallback(() => {
    setActiveViewId(null);
    setViewName('');
    setViewDescription('');
    setViewReportType('custom');
    setDraftSteps([]);
    setActiveStepIndex(0);
    setStepResults({});
    setViewParameters({});
    setActiveThreadId(null);
  }, []);

  // ── Before edit (restore/fork) — clear workspace for incoming forked results
  const handleBeforeEdit = useCallback(() => {
    setDraftSteps([]);
    setStepResults({});
  }, []);

  // ── Chat open/close handlers ────────────────────────────────────────────────
  const handleChatOpen = useCallback((itemId) => {
    if (itemId) {
      const view = views.find(v => v.id === itemId);
      setChatItemName(view?.name || 'Data View');
    } else {
      setChatItemName('New Chat');
    }
  }, [views]);

  const handleChatClose = useCallback(() => {
    setChatItemName('');
  }, []);

  // ── Parameter change -> re-execute with new values ────────────────────────
  const handleParameterChange = useCallback(async (key, value) => {
    const newParams = { ...viewParameters, [key]: value };
    setViewParameters(newParams);
    if (!draftSteps.length) return;
    setLoading(true);
    try {
      let result;
      if (activeViewId) {
        result = await dataViewApi.executeView(activeViewId, 'admin', 'default', newParams);
      } else {
        result = await dataViewApi.executeDraft({
          rf_ids: selectedCompanies.map(c => c.rf_id),
          steps: draftSteps,
          parameters: newParams,
        });
      }
      const sr = result.step_results || result;
      const dict = {};
      const items = Array.isArray(sr) ? sr : Object.values(sr);
      items.forEach(r => {
        if (!r?.step_name) return;
        const cols = r.columns || [];
        const objRows = (r.rows || []).map(row =>
          Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
        );
        dict[r.step_name] = { ...r, rows: objRows };
      });
      setStepResults(dict);
    } catch (err) {
      console.error('Failed to re-execute with params:', err);
    } finally {
      setLoading(false);
    }
  }, [viewParameters, draftSteps, activeViewId, selectedCompanies]);

  // ── SSE step_result from chat ────────────────────────────────────────────
  const handleStepResult = useCallback((data) => {
    const stepName = data.step_name || data.name || data.step?.name;
    if (!stepName) return;

    setDraftSteps(prev => {
      const existing = prev.findIndex(s => s.name === stepName);
      if (existing >= 0) return prev;
      return [...prev, { name: stepName, query: data.query || {} }];
    });

    if (data.columns && data.status === 'ok') {
      const cols = data.columns || [];
      const objRows = (data.rows || []).map(row =>
        Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
      );
      setStepResults(prev => ({ ...prev, [stepName]: { ...data, rows: objRows } }));
    }

    if (data.view_name) setViewName(data.view_name);
    if (data.view_description) setViewDescription(data.view_description);
  }, []);

  // ── Run query ────────────────────────────────────────────────────────────
  const handleRunQuery = useCallback(async () => {
    if (!draftSteps.length) return;
    if (!selectedCompanies.length && !activeViewId) {
      alert('Please select at least one company');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (activeViewId) {
        result = await dataViewApi.executeView(activeViewId);
      } else {
        result = await dataViewApi.executeDraft({
          rf_ids: selectedCompanies.map(c => c.rf_id),
          steps: draftSteps,
        });
      }
      const sr = result.step_results || result;
      const dict = {};
      const items = Array.isArray(sr) ? sr : Object.values(sr);
      items.forEach(r => {
        if (!r?.step_name) return;
        const cols = r.columns || [];
        const objRows = (r.rows || []).map(row => {
          if (Array.isArray(row)) {
            const obj = {};
            cols.forEach((col, i) => { obj[col] = row[i] ?? null; });
            return obj;
          }
          return row;
        });
        dict[r.step_name] = { ...r, rows: objRows };
      });
      setStepResults(dict);
    } catch (err) {
      console.error('Failed to run query:', err);
    } finally {
      setLoading(false);
    }
  }, [activeViewId, draftSteps, selectedCompanies]);

  // ── Save view ────────────────────────────────────────────────────────────
  const handleSaveView = useCallback(async () => {
    if (!viewName.trim() || !draftSteps.length) return;
    setLoading(true);
    try {
      const payload = {
        name: viewName.trim(),
        description: viewDescription,
        steps: draftSteps,
        rf_ids: selectedCompanies.map(c => c.rf_id),
        thread_id: activeThreadId,
        org_id: 'default',
        created_by: 'admin',
      };
      if (activeViewId) {
        await dataViewApi.updateView(activeViewId, payload);
      } else {
        const created = await dataViewApi.createView(payload);
        setActiveViewId(created.id);
        if (created.thread_id) setActiveThreadId(created.thread_id);
      }
      await loadViews();
    } catch (err) {
      console.error('Failed to save view:', err);
    } finally {
      setLoading(false);
    }
  }, [activeViewId, activeThreadId, viewName, viewDescription, draftSteps, selectedCompanies, loadViews]);

  // ── Workspace action from agent ─────────────────────────────────────────
  const handleWorkspaceAction = useCallback(async (action) => {
    console.log('[DataViewsPage] handleWorkspaceAction called:', action);
    const { action: act, view_id } = action || {};
    if (act === 'reload_view' && view_id) {
      try {
        const view = await dataViewApi.getView(view_id);
        console.log('[DataViewsPage] reloaded view steps:', view.steps?.map(s => s.name));
        setActiveViewId(view_id);
        setViewName(view.name || '');
        setViewDescription(view.description || '');
        setViewReportType(view.report_type || 'custom');
        setDraftSteps(view.steps || []);
        if (view.rf_ids?.length) {
          setCompanies(resolveCompanies(view.rf_ids));
        }
        setActiveThreadId(view.thread_id || null);
        if (view.steps?.length) {
          setLoading(true);
          try {
            const result = await dataViewApi.executeView(view_id);
            const sr = result.step_results || result;
            const dict = {};
            const items = Array.isArray(sr) ? sr : Object.values(sr);
            items.forEach(r => {
              if (!r?.step_name) return;
              const cols = r.columns || [];
              const objRows = (r.rows || []).map(row =>
                Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
              );
              dict[r.step_name] = { ...r, rows: objRows };
            });
            setStepResults(dict);
          } catch { } finally { setLoading(false); }
        }
      } catch (err) {
        console.error('Failed to reload view:', err);
      }
    } else if (act === 'refresh_view_list') {
      await loadViews();
    } else if (act === 'show_step_result') {
      const resultData = action.data;
      if (resultData) {
        const sr = resultData.step_results || (Array.isArray(resultData) ? resultData : [resultData]);
        const dict = {};
        const items = Array.isArray(sr) ? sr : Object.values(sr);
        items.forEach(r => {
          if (!r?.step_name) return;
          const cols = r.columns || [];
          const objRows = (r.rows || []).map(row =>
            Array.isArray(row) ? Object.fromEntries(cols.map((c, i) => [c, row[i] ?? null])) : row
          );
          dict[r.step_name] = { ...r, rows: objRows };
          setDraftSteps(prev => {
            const existing = prev.findIndex(s => s.name === r.step_name);
            if (existing < 0) {
              return [...prev, { name: r.step_name, query: {} }];
            }
            return prev;
          });
        });
        setStepResults(prev => ({ ...prev, ...dict }));
      }
    } else if (act === 'clear_workspace') {
      setActiveViewId(null);
      setViewName('');
      setViewDescription('');
      setViewReportType('custom');
      setDraftSteps([]);
      setStepResults({});
    }
  }, [loadViews, resolveCompanies, setCompanies]);

  // ── Delete view ──────────────────────────────────────────────────────────
  const handleDeleteView = useCallback(async (id) => {
    try {
      await dataViewApi.deleteView(id);
      if (activeViewId === id) {
        setActiveViewId(null);
        setViewName('');
        setViewDescription('');
        setViewReportType('custom');
        setDraftSteps([]);
        setStepResults({});
      }
      await loadViews();
    } catch (err) {
      console.error('Failed to delete view:', err);
    }
  }, [activeViewId, loadViews]);

  // ── KPI extra content for sidebar ───────────────────────────────────────
  const kpiExtraContent = (
    <div className="border-t border-zinc-200 shrink-0">
      <button
        onClick={() => setKpiOpen(!kpiOpen)}
        className="w-full h-9 flex items-center gap-2 px-4 hover:bg-zinc-100/60 transition-colors"
      >
        {kpiOpen
          ? <ChevronDown size={12} className="text-zinc-400" />
          : <ChevronRight size={12} className="text-zinc-400" />
        }
        <BarChart3 size={13} className="text-zinc-500" />
        <span className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">KPIs</span>
      </button>
      {kpiOpen && (
        <div className="pb-2 max-h-64 overflow-y-auto">
          <KpiPanel />
        </div>
      )}
    </div>
  );

  // Sidebar items need name + id at minimum for ItemSidebar
  const sidebarItems = views.map(v => ({
    id: v.id,
    name: v.name || 'Untitled View',
    report_type: v.report_type,
    scope: v.scope,
  }));

  // ── Derived values ──────────────────────────────────────────────────────
  const activeStep = draftSteps?.[activeStepIndex];
  const cubeQuery = activeStep?.query || null;
  const resultKey = activeStep?.name;
  const resultData = (resultKey && stepResults?.[resultKey]) || null;
  const columns = resultData?.columns || [];
  const rows = resultData?.rows || [];
  const isFinancialReport = ['pnl', 'balance_sheet', 'cash_flow'].includes(resultData?.report_type);

  // ── Export handler ─────────────────────────────────────────────────────
  const handleExport = useCallback(async ({ columns: expCols, rows: expRows, title }) => {
    if (isFinancialReport) {
      const DATA_ENGINE = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
      const r = await fetch(`${DATA_ENGINE}/api/export/report-to-sheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          universal_table: { columns: expCols, rows: expRows },
          title: title || 'Financial Report',
          report_type: resultData?.report_type || 'pnl',
        }),
      });
      const json = await r.json();
      const result = json.data || json;
      return result.spreadsheetUrl || result.spreadsheet_url || null;
    } else {
      const result = await dataViewApi.exportToSheet(title || 'Data Export', expCols, expRows);
      return result.spreadsheetUrl || result.spreadsheet_url || null;
    }
  }, [isFinancialReport, resultData?.report_type]);

  return (
    <AgentPageLayout
      sidebar={
        <ItemSidebar
          title="Data Views"
          items={sidebarItems}
          activeId={activeViewId}
          onSelectItem={handleSelectView}
          onDeleteItem={handleDeleteView}
          extraContent={kpiExtraContent}
          chat={
            <DataViewChat
              selectedCompanies={selectedCompanies}
              onSelectCompany={addCompany}
              onRemoveCompany={removeCompany}
              onStepResult={handleStepResult}
              onWorkspaceAction={handleWorkspaceAction}
              onBeforeEdit={handleBeforeEdit}
              threadId={activeThreadId}
              onThreadCreated={setActiveThreadId}
              currentSteps={draftSteps}
            />
          }
          onChatOpen={handleChatOpen}
          onChatClose={handleChatClose}
          chatItemName={chatItemName}
        />
      }
    >
      <Workspace
        title={viewName || 'Untitled View'}
        description={viewDescription}
        onRun={handleRunQuery}
        runLabel="Run Query"
        loading={loading}
        actions={
          <>
            <button onClick={() => setEditorOpen(v => !v)} title="Toggle Query" className={th.workspace.titleBar.actionBtn}>
              <Code size={14} />
            </button>
            <button onClick={handleSaveView} disabled={loading} title="Save View" className={th.workspace.titleBar.actionBtn}>
              <Save size={14} />
            </button>
            {rows.length > 0 && (
              <button onClick={() => setFullscreen(true)} title="Expand Table" className={th.workspace.titleBar.actionBtn}>
                <Maximize2 size={14} />
              </button>
            )}
          </>
        }
        toolbar={
          <>
            {editorOpen && <QueryViewer query={cubeQuery} stepName={activeStep?.name} />}
            {activeStep?.parameters && Object.keys(activeStep.parameters).length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2 border-b border-zinc-200 bg-zinc-50/80 flex-shrink-0 flex-wrap">
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Params</span>
                {Object.entries(activeStep.parameters).map(([key, defn]) => {
                  const value = viewParameters?.[key] ?? defn.default ?? '';
                  if (defn.type === 'select' && defn.options) {
                    return (
                      <label key={key} className="flex items-center gap-1.5 text-xs text-zinc-600">
                        <span className="text-zinc-500">{key.replace(/_/g, ' ')}:</span>
                        <select
                          value={value}
                          onChange={(e) => handleParameterChange?.(key, e.target.value)}
                          className="border border-zinc-200 rounded px-1.5 py-0.5 text-xs bg-white text-zinc-700 focus:outline-none focus:border-accent"
                        >
                          {defn.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  return (
                    <label key={key} className="flex items-center gap-1.5 text-xs text-zinc-600">
                      <span className="text-zinc-500">{key.replace(/_/g, ' ')}:</span>
                      <input
                        type={defn.type === 'date' ? 'date' : 'text'}
                        value={value}
                        onChange={(e) => handleParameterChange?.(key, e.target.value)}
                        className="border border-zinc-200 rounded px-1.5 py-0.5 text-xs bg-white text-zinc-700 w-28 focus:outline-none focus:border-accent"
                      />
                    </label>
                  );
                })}
              </div>
            )}
          </>
        }
        footer={activeViewId && viewReportType !== 'custom' && (
          <ReconciliationPanel viewId={activeViewId} reportType={viewReportType} />
        )}
      >
        <DataTable
          columns={columns}
          rows={rows}
          mode={isFinancialReport ? 'financial' : 'flat'}
          title={viewName}
          onCellClick={!isFinancialReport ? (cell) => setDrillCell(cell) : undefined}
          onExport={handleExport}
          loading={loading}
          emptyMessage="Run a query to see results"
          chartSlot={isFinancialReport ? <PnlChartView columns={columns} rows={rows} /> : undefined}
        />
      </Workspace>

      {/* Drill-down panel (flat mode only) */}
      <DrillDownPanel
        open={!!drillCell}
        cell={drillCell}
        onClose={() => setDrillCell(null)}
        rfIds={selectedCompanies.map(c => c.rf_id)}
        parentTimeDimensions={activeStep?.query?.timeDimensions || []}
      />

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="h-12 flex items-center justify-between px-5 border-b border-zinc-200">
            <span className="text-sm font-semibold text-zinc-800">{viewName || 'Results'} — {rows.length} rows</span>
            <button onClick={() => setFullscreen(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-200 rounded-md text-zinc-600 hover:bg-zinc-50">
              <Minimize2 size={12} /> Exit Fullscreen
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <DataTable columns={columns} rows={rows} mode={isFinancialReport ? 'financial' : 'flat'} title={viewName} />
          </div>
        </div>
      )}
    </AgentPageLayout>
  );
}
